// src/components/BaseLayout.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/BaseLayout.css';

// The six primary destinations, in one place so the desktop row and the mobile
// menu cannot drift apart.
const NAV_LINKS = [
  { to: '/home', label: 'Home' },
  { to: '/training', label: 'Training' },
  { to: '/exercises', label: 'Exercises' },
  { to: '/create-program', label: 'Programs' },
  { to: '/coaching', label: 'Coaching' },
  { to: '/my-training', label: 'Maxes' },
];

const BaseLayout = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();

  // The mobile nav.
  //
  // # WHY A MENU AND NOT THE SCROLLER IT REPLACES
  //
  // The bar previously became a horizontally scrollable row of pills on a phone,
  // with the scrollbar hidden. Every link was technically reachable, but only by
  // a swipe nothing signalled — "Programs" was cut off mid-word and Coaching and
  // Maxes were off-screen with no affordance that they existed. For a phone-first
  // app that is the primary navigation being effectively hidden. A disclosure
  // button is discoverable and conventional; the links live behind a tap, not a
  // guess.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const menuId = 'primary-nav';
  const navRef = useRef(null);

  // Close on navigation: without this, tapping a link changes the route while
  // the menu stays open over the page it just went to.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Escape closes it, and a click outside dismisses it — the two things a person
  // expects of an open menu, and neither of which the scroller needed.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menuOpen]);

  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="base-layout">
      <nav className="navbar" ref={navRef}>
        <div className="nav-brand">
          {/* The wordmark is wrapped so it can collapse on narrow screens,
              leaving the logo mark (a ::before monogram) carrying the brand. */}
          <Link to="/">
            <span className="brand-wordmark">Loenhart Training Systems</span>
          </Link>
        </div>

        {isAuthenticated ? (
          <>
            {/* Shown only on a phone (CSS hides it on desktop). Controls the same
                link list the desktop row renders. */}
            <button
              type="button"
              className="nav-toggle"
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="nav-toggle-bar" aria-hidden="true" />
              <span className="nav-toggle-bar" aria-hidden="true" />
              <span className="nav-toggle-bar" aria-hidden="true" />
            </button>

            <div
              id={menuId}
              className={`nav-links nav-links--menu${menuOpen ? ' is-open' : ''}`}
            >
              {NAV_LINKS.map((link) => (
                <Link key={link.to} to={link.to}>{link.label}</Link>
              ))}
              {/* Logout lives HERE, not on a home screen, because there are two
                  homes. It used to sit inside the coach home's Account card,
                  which meant every athlete — anybody with an assignment, and now
                  every brand-new account — had no way to sign out at all. */}
              <button type="button" onClick={handleLogout} className="btn-ghost nav-logout">
                Log out
              </button>
            </div>
          </>
        ) : (
          <div className="nav-links">
            <Link to="/login">Login</Link>
          </div>
        )}
      </nav>
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default BaseLayout;
